import './style.scss';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { getAcadamicLevelShortHand, mapError } from 'utils/func';

import { useGetAllEmployees } from 'utils/hooks/employees';
import ScientistCard from 'features/Scientist/components/ScientistCard';
import { InputText } from 'primereact/inputtext';
import ScientistSkeleton from 'features/Scientist/components/ScientistSkeleton';
import ScientistPagination from 'features/Scientist/components/ScientistPagination';

export default function ScientistViewList() {
  // #region Data
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: 12,
  });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [appliedSearch, setAppliedSearch] = useState(search);

  const { data, isLoading, isError, error } = useGetAllEmployees(pagination, appliedSearch, {
    refetchOnWindowFocus: false,
  });

  // #endregion

  // #region Event

  useEffect(() => {
    if (searchParams.get('search') !== null) {
      setAppliedSearch(searchParams.get('search').trim() || '');
    }
  }, [searchParams.get('search')]);

  // #endregion

  const formatEmail = (email) =>
    email ? (
      <>
        {email.split('@')[0]}
        <wbr />@{email.split('@')[1]}
      </>
    ) : (
      ''
    );

  const formatFullname = (value) => {
    const publicProfileId = value?.contacts?.[0]?.scientificProfile?.publicProfile?.publicProfileId;
    if (publicProfileId) {
      return (
        <a
          href={`/profile/${publicProfileId}`}
          target="_blank"
          rel="noreferrer"
          className="inline-block"
        >
          <div>
            {getAcadamicLevelShortHand(
              value?.contacts?.[0]?.scientificProfile?.academicRank,
              value?.contacts?.[0]?.scientificProfile?.academicDegree,
              t,
              true
            )}
            {value?.contacts?.[0]?.fullname}
          </div>
        </a>
      );
    }

    return (
      <div>
        {getAcadamicLevelShortHand(
          value?.contacts?.[0]?.scientificProfile?.academicRank,
          value?.contacts?.[0]?.scientificProfile?.academicDegree,
          t,
          true
        )}
        {value?.contacts?.[0]?.fullname}
      </div>
    );
  };

  const formatOrganization = (value) => {
    if (value?.contacts?.[0]?.organization?.englishName === 'Other') {
      return value?.contacts?.[0]?.specificOrganizationName;
    }
    return t('lang') === 'vi'
      ? value?.contacts?.[0]?.organization?.name
      : value?.contacts?.[0]?.organization?.englishName;
  };

  const formatResearchField = (value) => (
    <div>
      {value?.userResearchFields?.map((field, index) => (
        <span className="p-tag-value" key={field?.id}>
          {t(`researchFields.${field?.researchField?.code}`)}
          {index < (value?.userResearchFields?.length || 0) - 1 ? ' - ' : ''}
        </span>
      ))}
    </div>
  );

  const render = () => {
    if (isLoading) {
      return (
        <div className="grid">
          {Array.from({ length: 6 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="col-12 lg:col-6 xl:col-4">
              <ScientistSkeleton />
            </div>
          ))}
        </div>
      );
    }
    if (data.data?.length === 0) {
      return <p>{t('employee.viewList.noDataMessage')}</p>;
    }
    return (
      <div className="grid">
        {data.data?.map((scientist) => (
          <div key={scientist.id} className="col-12 lg:col-6 xl:col-4">
            <ScientistCard
              data={scientist}
              formatFullname={formatFullname}
              formatEmail={formatEmail}
              formatOrganization={formatOrganization}
              formatResearchField={formatResearchField}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className="card relative scientist-view-list p-3 sm:p-4"
      style={{
        overflowY: 'auto',
        overflowX: 'hidden',
        maxHeight: 'calc(100vh - 128px)',
      }}
    >
      {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
      <div className="flex flex-column lg:flex-row justify-content-between lg:align-items-center pb-4">
        <h4 className="m-0 sm:block text-xl sm:text-2xl">{t('employee.viewList.title')}</h4>

        <div className="relative mt-5 lg:mt-2">
          <i className="pi pi-search search-icon" />
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSearchParams({ first: 0, search: e.target.value }, { replace: true });
              }
            }}
            placeholder={t('employee.viewList.searchPlaceholder')}
            className="pl-5 w-18rem sm:w-30rem"
          />
        </div>
      </div>
      {render()}

      <ScientistPagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data.pagination}
        pageName="employee"
      />
    </div>
  );
}
